/**
 * Collection Item Permission Utilities
 *
 * Centralized permission logic for actions on collection items.
 * Mirrors the backend implementation for consistent behavior.
 * Uses role-based access control: Reader < Contributor < Editor < Admin
 */

import { roleAtLeast, MemberRole } from './roles'

export const CollectionItemAction = {
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE_TAGS: 'manage_tags',
  PIN: 'pin',
  SHARE: 'share',
  COMMENT: 'comment',
  LIKE: 'like',
  READ_COMMENTS: 'read_comments',
  READ_LIKES: 'read_likes',
  COPY_TO_COLLECTION: 'copy_to_collection',
}

const PUBLIC_LOGGED_IN_ACTIONS = [
  CollectionItemAction.SHARE,
  CollectionItemAction.COMMENT,
  CollectionItemAction.LIKE,
  CollectionItemAction.READ_COMMENTS,
  CollectionItemAction.READ_LIKES,
  CollectionItemAction.COPY_TO_COLLECTION,
]

const PUBLIC_GUEST_ACTIONS = [
  CollectionItemAction.SHARE,
  CollectionItemAction.READ_COMMENTS,
  CollectionItemAction.READ_LIKES,
]

function checkRolePermission(role, isItemOwner, action) {
  if (action === CollectionItemAction.PIN) {
    return roleAtLeast(role, MemberRole.EDITOR)
  }

  if (
    action === CollectionItemAction.EDIT ||
    action === CollectionItemAction.DELETE ||
    action === CollectionItemAction.MANAGE_TAGS
  ) {
    if (roleAtLeast(role, MemberRole.EDITOR)) return true
    if (roleAtLeast(role, MemberRole.CONTRIBUTOR) && isItemOwner) return true
    return false
  }

  if (PUBLIC_LOGGED_IN_ACTIONS.includes(action)) {
    return roleAtLeast(role, MemberRole.READER)
  }

  return false
}

export function canPerformItemAction(context, action) {
  if (context.isPrivateCollection) {
    return true
  }

  const role = context.memberRole

  if (context.isPublicCollection) {
    if (context.isCollectionOwner) return true

    if (context.isMember && role) {
      return checkRolePermission(role, context.isItemOwner, action)
    }

    if (context.isLoggedIn) {
      return PUBLIC_LOGGED_IN_ACTIONS.includes(action)
    }

    return PUBLIC_GUEST_ACTIONS.includes(action)
  }

  if (context.isCollectionOwner) return true

  if (context.isMember && role) {
    return checkRolePermission(role, context.isItemOwner, action)
  }

  return false
}

export function buildPermissionContext({ membership, item, userId, collection }) {
  const isPrivateCollection = item?.isPrivate || collection?.private || false

  const isItemOwner = !!(userId && (
    item?.addedById === userId ||
    item?.addedBy?.id === userId ||
    item?.addedBy === userId
  ))

  return {
    isCollectionOwner: membership?.isOwner ?? false,
    isItemOwner,
    isPrivateCollection,
    isPublicCollection: collection?.isPublic ?? false,
    isLoggedIn: !!userId,
    isMember: !!membership,
    memberRole: membership?.role,
  }
}

/**
 * Check if a user can delete ANY comment on a collection item (moderation).
 *
 * Rule: private collection OR collection owner OR Editor+ member.
 * Comment-owner ("own comment") is orthogonal and handled at the call site.
 */
export function canDeleteAnyComment(context) {
  if (!context) return false
  if (context.isPrivateCollection) return true
  if (context.isCollectionOwner) return true
  if (context.isMember && context.memberRole) {
    return roleAtLeast(context.memberRole, MemberRole.EDITOR)
  }
  return false
}

/**
 * Bulk Add/Remove tags & soft-Delete on a collection.
 * Mirrors backend permission for POST /item/:collectionId/bulk/{add-tags|remove-tags|delete}.
 */
export function canBulkManageItems(context) {
  if (!context) return false
  if (context.isPrivateCollection) return true
  if (context.isCollectionOwner) return true
  if (context.isMember && context.memberRole) {
    return roleAtLeast(context.memberRole, MemberRole.EDITOR)
  }
  return false
}

/**
 * Compute pin icon display state for an item, shared across all item styles
 * (card, list, headline, tile) for both bookmarks and notes.
 *
 * - Filled pin: always shown when the item is pinned (informational, visible
 *   to every viewer including those who cannot pin).
 * - Outline pin: only shown when the viewer can pin and the item is not
 *   pinned — hides the "click to pin" affordance from members without pin
 *   rights in shared collections, and in public mode.
 */
export function getItemPinDisplay({ context, isPinned, publicMode }) {
  const canPin = !publicMode && !!context && canPerformItemAction(context, CollectionItemAction.PIN)
  return {
    canPin,
    showFilledPin: !!isPinned,
    showOutlinePin: !isPinned && canPin,
  }
}

export function getItemPermissions(context) {
  return {
    [CollectionItemAction.EDIT]: canPerformItemAction(context, CollectionItemAction.EDIT),
    [CollectionItemAction.DELETE]: canPerformItemAction(context, CollectionItemAction.DELETE),
    [CollectionItemAction.MANAGE_TAGS]: canPerformItemAction(context, CollectionItemAction.MANAGE_TAGS),
    [CollectionItemAction.SHARE]: canPerformItemAction(context, CollectionItemAction.SHARE),
    [CollectionItemAction.COMMENT]: canPerformItemAction(context, CollectionItemAction.COMMENT),
    [CollectionItemAction.LIKE]: canPerformItemAction(context, CollectionItemAction.LIKE),
    [CollectionItemAction.READ_COMMENTS]: canPerformItemAction(context, CollectionItemAction.READ_COMMENTS),
    [CollectionItemAction.READ_LIKES]: canPerformItemAction(context, CollectionItemAction.READ_LIKES),
    [CollectionItemAction.COPY_TO_COLLECTION]: canPerformItemAction(context, CollectionItemAction.COPY_TO_COLLECTION),
    [CollectionItemAction.PIN]: canPerformItemAction(context, CollectionItemAction.PIN),
  }
}
